import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  SAVED_VIEW_COPY,
  deleteConfirmCopy,
  viewOptionsLabel,
} from '../../containers/records/shared/savedViews/savedViewFilters';
import SaveViewButton, { SavedViewNameForm, popoverAlignClass, useDismissablePopover } from './SaveViewButton';
import './savedViewsBar.scss';

// ENG-3551 Views strip. Presentational: the page's useSavedViews hook owns data
// and effects; every deliberate interaction is reported through onInteraction
// (analytics action enum only — never names, ids or filter values). Names are
// rendered as React text, never HTML.

const SavedViewChip = ({
  view, selected, saving, onApply, onRename, onDelete, onInteraction, onFocusLost,
}) => {
  const [mode, setMode] = useState(null); // null | 'menu' | 'rename' | 'delete'
  const [alignClass, setAlignClass] = useState('');
  const containerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const firstItemRef = useRef(null);

  const close = useCallback((reason) => {
    if (mode === 'rename' && reason) onInteraction('cancel_rename');
    if (mode === 'delete' && reason) onInteraction('cancel_delete');
    setMode(null);
    if (reason !== 'outside' && menuButtonRef.current) menuButtonRef.current.focus();
  }, [mode, onInteraction]);

  useDismissablePopover({ open: mode !== null, onDismiss: close, containerRef });

  const toggleMenu = () => {
    if (mode) {
      close('button');
      return;
    }
    onInteraction('open_options');
    setAlignClass(popoverAlignClass(containerRef.current));
    setMode('menu');
    setTimeout(() => firstItemRef.current?.focus(), 0);
  };

  const submitRename = async (name) => {
    onInteraction('rename');
    const result = await onRename(view.id, name);
    if (result?.ok) {
      setMode(null);
      menuButtonRef.current?.focus();
    }
    return result;
  };

  const confirmDelete = async () => {
    onInteraction('delete');
    const result = await onDelete(view.id);
    setMode(null);
    if (!result?.ok) menuButtonRef.current?.focus();
    else onFocusLost();
  };

  const handleMenuKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = Array.from(containerRef.current?.querySelectorAll('[role="menuitem"]') || []);
    const index = items.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
    items[(next + items.length) % items.length]?.focus();
  };

  return (
    <li className={`saved-view-chip${selected ? ' saved-view-chip--selected' : ''}`} ref={containerRef}>
      <button
        type="button"
        className="saved-view-chip__apply"
        aria-pressed={selected}
        title={view.name}
        onClick={() => onApply(view)}
      >
        <span className="saved-view-chip__name">{view.name}</span>
      </button>
      <button
        type="button"
        ref={menuButtonRef}
        className="saved-view-chip__menu"
        aria-label={viewOptionsLabel(view.name)}
        aria-haspopup="menu"
        aria-expanded={mode !== null}
        onClick={toggleMenu}
      >
        ⋯
      </button>
      {mode === 'menu' && (
        <div
          className={`saved-views-popover saved-views-popover--menu${alignClass}`}
          role="menu"
          tabIndex={-1}
          aria-label={viewOptionsLabel(view.name)}
          onKeyDown={handleMenuKeyDown}
        >
          <button
            type="button"
            role="menuitem"
            ref={firstItemRef}
            className="saved-views-menu__item"
            onClick={() => { onInteraction('open_rename'); setMode('rename'); }}
          >
            {SAVED_VIEW_COPY.rename}
          </button>
          <button
            type="button"
            role="menuitem"
            className="saved-views-menu__item saved-views-menu__item--danger"
            onClick={() => { onInteraction('open_delete'); setMode('delete'); }}
          >
            {SAVED_VIEW_COPY.delete}
          </button>
        </div>
      )}
      {mode === 'rename' && (
        <div className={`saved-views-popover${alignClass}`} role="dialog" aria-label={SAVED_VIEW_COPY.renameTitle}>
          <SavedViewNameForm
            title={SAVED_VIEW_COPY.renameTitle}
            initialName={view.name}
            saving={saving}
            onSubmit={submitRename}
            onCancel={close}
          />
        </div>
      )}
      {mode === 'delete' && (
        <div className={`saved-views-popover${alignClass}`} role="alertdialog" aria-label={deleteConfirmCopy(view.name)}>
          <p className="saved-views-form__confirm">{deleteConfirmCopy(view.name)}</p>
          <div className="saved-views-form__actions">
            <button
              type="button"
              className="saved-views-btn"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onClick={() => close('button')}
            >
              {SAVED_VIEW_COPY.cancel}
            </button>
            <button
              type="button"
              className="saved-views-btn saved-views-btn--danger"
              disabled={saving}
              onClick={confirmDelete}
            >
              {SAVED_VIEW_COPY.delete}
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

SavedViewChip.propTypes = {
  view: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), name: PropTypes.string }).isRequired,
  selected: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onInteraction: PropTypes.func.isRequired,
  onFocusLost: PropTypes.func.isRequired,
};

export const SavedViewsNotice = ({ notice, onRetry, onDismiss }) => (
  <div className="saved-views-notice" role="status">
    <span className="saved-views-notice__message">{notice.message}</span>
    {notice.retry && (
      <button type="button" className="saved-views-bar__action" onClick={onRetry}>
        {SAVED_VIEW_COPY.retry}
      </button>
    )}
    <button
      type="button"
      className="saved-views-notice__dismiss"
      aria-label={SAVED_VIEW_COPY.dismissNotice}
      onClick={onDismiss}
    >
      ×
    </button>
  </div>
);

SavedViewsNotice.propTypes = {
  notice: PropTypes.shape({ message: PropTypes.string, retry: PropTypes.func }).isRequired,
  onRetry: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

const SavedViewsBar = ({
  views,
  selectedView,
  isModified,
  saving,
  notice,
  summary,
  onApply,
  onUpdate,
  onSaveAsNew,
  onRevert,
  onRename,
  onDelete,
  onRetry,
  onDismissNotice,
  onInteraction,
  focusRequest,
}) => {
  const rootRef = useRef(null);
  const [localFocusRequest, setLocalFocusRequest] = useState(0);

  // A save or delete unmounts the control that had focus (Save view hides once a
  // view is selected; a deleted chip disappears). Move focus to the selected chip,
  // or the first chip, instead of dropping it to <body>.
  useEffect(() => {
    if (!focusRequest && !localFocusRequest) return;
    const root = rootRef.current;
    const target = root?.querySelector('.saved-view-chip--selected .saved-view-chip__apply')
      || root?.querySelector('.saved-view-chip__apply');
    target?.focus();
  }, [focusRequest, localFocusRequest]);

  const handleApply = (view) => {
    onInteraction('apply');
    onApply(view);
  };
  const handleRetry = () => {
    onInteraction('retry');
    onRetry();
  };
  const handleDismiss = () => {
    onInteraction('dismiss_notice');
    onDismissNotice();
  };

  if (views.length === 0 && !notice) return null;

  return (
    <div className="saved-views-bar" ref={rootRef}>
      {views.length > 0 && (
        <div className="saved-views-bar__strip">
          <span className="saved-views-bar__label" aria-hidden="true">{SAVED_VIEW_COPY.strip}</span>
          <ul className="saved-views-bar__chips" aria-label={SAVED_VIEW_COPY.strip}>
            {views.map((view) => (
              <SavedViewChip
                key={view.id}
                view={view}
                selected={Boolean(selectedView && String(selectedView.id) === String(view.id))}
                saving={saving}
                onApply={handleApply}
                onRename={onRename}
                onDelete={onDelete}
                onInteraction={onInteraction}
                onFocusLost={() => setLocalFocusRequest((count) => count + 1)}
              />
            ))}
          </ul>
          {selectedView && isModified && (
            <span className="saved-views-bar__modified">
              <span className="saved-views-bar__modified-label">{SAVED_VIEW_COPY.modified}</span>
              <button
                type="button"
                className="saved-views-bar__action"
                disabled={saving}
                onClick={() => { onInteraction('update'); onUpdate(); }}
              >
                {SAVED_VIEW_COPY.updateView}
              </button>
              <SaveViewButton
                label={SAVED_VIEW_COPY.saveAsNew}
                variant="link"
                saving={saving}
                onOpen={() => onInteraction('open_save_as_new')}
                onCancel={() => onInteraction('cancel_save')}
                onSave={async (name) => {
                  onInteraction('save');
                  const result = await onSaveAsNew(name);
                  if (result?.ok) setLocalFocusRequest((count) => count + 1);
                  return result;
                }}
              />
              <button
                type="button"
                className="saved-views-bar__action"
                disabled={saving}
                onClick={() => { onInteraction('revert'); onRevert(); }}
              >
                {SAVED_VIEW_COPY.revert}
              </button>
            </span>
          )}
        </div>
      )}
      {selectedView && (
        <p className="saved-views-bar__summary">{`${SAVED_VIEW_COPY.activeFiltersPrefix}${summary}`}</p>
      )}
      {notice && <SavedViewsNotice notice={notice} onRetry={handleRetry} onDismiss={handleDismiss} />}
    </div>
  );
};

SavedViewsBar.propTypes = {
  views: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedView: PropTypes.object,
  isModified: PropTypes.bool,
  saving: PropTypes.bool,
  notice: PropTypes.shape({ message: PropTypes.string, retry: PropTypes.func }),
  summary: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onSaveAsNew: PropTypes.func.isRequired,
  onRevert: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onDismissNotice: PropTypes.func.isRequired,
  onInteraction: PropTypes.func,
  focusRequest: PropTypes.number,
};

SavedViewsBar.defaultProps = {
  selectedView: null,
  isModified: false,
  saving: false,
  notice: null,
  summary: SAVED_VIEW_COPY.activeFiltersNone,
  onInteraction: () => {},
  focusRequest: 0,
};

// Successful-empty list state, shown by the records HOC only on an enabled page.
// Clear filters calls the filter bar's own Clear all.
export const SavedViewsEmptyState = ({ onClear }) => (
  <div className="saved-views-empty" role="status">
    <p className="saved-views-empty__title">{SAVED_VIEW_COPY.emptyTitle}</p>
    <button type="button" className="saved-views-btn" onClick={onClear}>
      {SAVED_VIEW_COPY.clearFilters}
    </button>
  </div>
);

SavedViewsEmptyState.propTypes = {
  onClear: PropTypes.func.isRequired,
};

export default SavedViewsBar;
